import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { useAppContext } from '../../../contexts/AppContext';
import { generateJidVariants } from '../../../utils/phoneUtils';
import { emitConversationMetaChanged } from '../utils/partykitSocket';
import { scopeConversationQuery } from '../lib/conversationScope';

/**
 * Marca conversa ativa como lida.
 * Escopo por connection_id quando existir — evita zerar unread da conexão irmã.
 */
export const useChatMarkAsRead = (
    activeConversationId: string | null,
    connectionId?: string | null,
    updateConversationState?: (
        id: string,
        updates: { unreadCount: number },
        connectionId?: string | null,
    ) => void,
) => {
    const { currentOrganization } = useOrganization();
    const { currentPage } = useAppContext();
    const isChatActive = currentPage === 'chat';
    const lastMarkedRef = useRef<string | null>(null);
    const markingInProgressRef = useRef(false);

    const markKey = activeConversationId
        ? `${connectionId || ''}:${activeConversationId}`
        : null;

    const doMarkAsRead = useCallback(async () => {
        if (!activeConversationId || !currentOrganization?.id) return;
        if (markingInProgressRef.current) return;
        markingInProgressRef.current = true;

        try {
            if (updateConversationState) {
                updateConversationState(activeConversationId, { unreadCount: 0 }, connectionId);
            }

            let query = supabase
                .from('chat_conversations')
                .update({ unread_count: 0 })
                .in('remote_jid', generateJidVariants(activeConversationId));
            query = scopeConversationQuery(
                query,
                connectionId ?? null,
                currentOrganization.id,
            );

            const { error } = await query;

            if (error) {
                console.error('[MarkAsRead] ❌ Erro DB:', error.message);
            } else {
                const isInstagram = activeConversationId.startsWith('ig:');
                const phone = isInstagram
                    ? undefined
                    : activeConversationId.replace('@s.whatsapp.net', '').replace(/\D/g, '') || undefined;
                emitConversationMetaChanged({
                    organizationId: currentOrganization.id,
                    phone,
                    remoteJid: activeConversationId,
                    connectionId: connectionId ?? undefined,
                    unreadCount: 0,
                });
            }

            lastMarkedRef.current = markKey;
        } catch (error) {
            console.error('[MarkAsRead] ❌ Exceção:', error);
        } finally {
            markingInProgressRef.current = false;
        }
    }, [
        activeConversationId,
        connectionId,
        currentOrganization?.id,
        updateConversationState,
        markKey,
    ]);

    useEffect(() => {
        if (!isChatActive || !activeConversationId || !currentOrganization?.id || !markKey) return;
        if (lastMarkedRef.current === markKey) return;

        void doMarkAsRead();
    }, [isChatActive, activeConversationId, currentOrganization?.id, markKey, doMarkAsRead]);

    useEffect(() => {
        return () => { lastMarkedRef.current = null; };
    }, [markKey]);
};
